import { useState, useEffect, useRef } from 'preact/hooks';
import { Verse } from './Verse.js';
import { VerseMenu } from './VerseMenu.js';
import { WatchLink } from './WatchLink.js';
import { AudioOnlyChapter } from './AudioOnlyChapter.js';
import { askOpen, askSeedRef } from './Ask.js';
import { karaoke } from '../karaoke/highlight.js';
import { playback } from '../state/store.js';
import { publisher } from '../publisher/mode.js';
import { readingPrefs } from '../state/reading-prefs.js';
import type { ReaderChapter } from '../state/chapter-store.js';
import type { ChapterVerse } from '../audio/biblebrain.js';
import { saveReadingPosition, consumePendingScroll } from '../state/reading-position.js';

type Props = {
  chapter: ReaderChapter;
};

export function Chapter({ chapter }: Props) {
  // Audio-only branch: the active publisher manifest declares hasText:false,
  // OR the chapter has no verses at all (defensive — catches stale bakes and
  // BB outages where verse fetch failed silently). The PlayerBar continues
  // to drive playback below; only the verse-list view is replaced.
  const pub = publisher.value;
  const isAudioOnly = pub?.hasText === false || chapter.verses.length === 0;
  if (isAudioOnly) {
    return <AudioOnlyChapter chapter={chapter} />;
  }
  return <ChapterWithVerses chapter={chapter} />;
}

// ─── Paragraph-mode helpers ──────────────────────────────────────────────────

type ParaGroup = {
  paraStyle: string;
  verses: ChapterVerse[];
};

/**
 * Split the verse array into paragraph groups. A new group starts whenever
 * `isFirstInPara === true`. Groups keep the `paraStyle` of their first verse.
 */
function groupVersesByPara(verses: ChapterVerse[]): ParaGroup[] {
  const groups: ParaGroup[] = [];
  let current: ParaGroup | null = null;
  for (const v of verses) {
    if (v.isFirstInPara === true || current === null) {
      current = { paraStyle: v.paraStyle ?? 'p', verses: [v] };
      groups.push(current);
    } else {
      current.verses.push(v);
    }
  }
  return groups;
}

// ─────────────────────────────────────────────────────────────────────────────

function ChapterWithVerses({ chapter }: Props) {
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const karaokeFrame = karaoke.value;
  const karaokeRef = karaokeFrame?.currentVerse?.verseRef ?? null;
  const karaokeProgress = karaokeFrame?.verseProgress ?? 0;
  const book = chapter.bookId;
  const lastScrolledRef = useRef<string | null>(null);
  const paragraphMode = readingPrefs.value.paragraphMode ?? false;

  // On mount: consume any pending scroll-to-verse (set by App on startup restore).
  useEffect(() => {
    const pending = consumePendingScroll();
    if (!pending) return;
    // Defer one frame so the verse list is in the DOM.
    const id = requestAnimationFrame(() => {
      const el = document.querySelector(
        `[data-verse-ref="${cssEscape(pending)}"]`,
      ) as HTMLElement | null;
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return () => cancelAnimationFrame(id);
  }, []);

  // Track topmost visible verse and persist it as the reading position.
  // Uses IntersectionObserver so there's zero scroll-listener overhead.
  useEffect(() => {
    const article = document.querySelector(
      `[data-chapter-key="${cssEscape(`${chapter.bookId}.${chapter.chapter}`)}"]`,
    );
    if (!article || typeof IntersectionObserver === 'undefined') return;

    const visibleSet = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const ref = (entry.target as HTMLElement).dataset['verseRef'];
          if (!ref) continue;
          if (entry.isIntersecting) {
            visibleSet.add(ref);
          } else {
            visibleSet.delete(ref);
          }
        }
        if (visibleSet.size === 0) return;
        // Pick the topmost verse among the currently visible ones.
        const verseOrder = chapter.verses.map((v) => v.ref);
        let topmost: string | null = null;
        for (const r of verseOrder) {
          if (visibleSet.has(r)) { topmost = r; break; }
        }
        if (!topmost) return;
        saveReadingPosition(chapter.bookId, chapter.chapter, topmost);
      },
      { threshold: 0.3 },
    );

    const verseEls = article.querySelectorAll('[data-verse-ref]');
    verseEls.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [chapter.bookId, chapter.chapter, chapter.verses]);

  useEffect(() => {
    if (!karaokeRef || !playback.value.playing) return;
    if (lastScrolledRef.current === karaokeRef) return;
    lastScrolledRef.current = karaokeRef;
    const el = document.querySelector(
      `[data-verse-ref="${cssEscape(karaokeRef)}"]`,
    ) as HTMLElement | null;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const dockHeight = document.querySelector('.player-dock')?.getBoundingClientRect().height ?? 160;
    const viewportH = window.innerHeight;
    const visibleBottom = viewportH - dockHeight - 24;
    const visibleTop = 80;
    if (rect.top >= visibleTop && rect.bottom <= visibleBottom) return;
    const target = window.scrollY + rect.top - viewportH / 2 + rect.height / 2;
    window.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
  }, [karaokeRef]);

  const activeVerse =
    activeMenu ? chapter.verses.find((v) => v.ref === activeMenu) ?? null : null;

  return (
    <article class="chapter" data-chapter-key={`${chapter.bookId}.${chapter.chapter}`}>
      <header class="chapter-header">
        <h1 class="chapter-title">{chapter.bookName} {chapter.chapter}</h1>
        <WatchLink book={chapter.bookId} chapter={chapter.chapter} languageIso={chapter.language.iso639_3} />
      </header>

      {/* Paragraph mode: active when the pref is on AND USX para data exists.
          Falls back silently to the verse-list path if data is unavailable. */}
      {paragraphMode && chapter.verses.some((v) => v.isFirstInPara !== undefined) ? (
        <div class="chapter-paras">
          {(() => {
            const groups = groupVersesByPara(chapter.verses);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const nodes: any[] = [];
            for (const group of groups) {
              // Section headings come from the first verse of the group (or any
              // verse in the group that carries headingBefore).
              for (const v of group.verses) {
                if (v.headingBefore) {
                  nodes.push(
                    <p key={`heading-${v.ref}`} class="section-heading">
                      {v.headingBefore}
                    </p>,
                  );
                }
              }
              nodes.push(
                <div
                  key={`para-${group.verses[0]?.ref ?? nodes.length}`}
                  class={`chapter-para para-${group.paraStyle}`}
                >
                  {group.verses.map((v) => (
                    <Verse
                      key={v.ref}
                      verse={v}
                      lang={chapter.language.bcp47}
                      book={book}
                      active={v.ref === karaokeRef}
                      progress={v.ref === karaokeRef ? karaokeProgress : 0}
                      onTap={(ref) => setActiveMenu(ref)}
                      inline={true}
                    />
                  ))}
                </div>,
              );
            }
            return nodes;
          })()}
        </div>
      ) : (
        <div class="chapter-verses">
          {chapter.verses.map((v) => (
            <Verse
              key={v.ref}
              verse={v}
              lang={chapter.language.bcp47}
              book={book}
              active={v.ref === karaokeRef}
              progress={v.ref === karaokeRef ? karaokeProgress : 0}
              onTap={(ref) => setActiveMenu(ref)}
            />
          ))}
        </div>
      )}

      {activeVerse ? (
        <VerseMenu
          chapter={chapter}
          verse={activeVerse}
          onClose={() => setActiveMenu(null)}
          onAsk={(ref) => {
            askSeedRef.value = ref;
            askOpen.value = true;
          }}
        />
      ) : null}
    </article>
  );
}

function cssEscape(value: string): string {
  if (typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(value);
  return value.replace(/["\\]/g, '\\$&');
}
